import { Column, Row, Section, Text } from '@ydistri/ds';
import { formatDate } from '@ydistri/generic-utils';
import { parseISO } from 'date-fns';
import React, { useMemo } from 'react';
import {
  useDisplayStoreComparator,
  useDisplayStoreLabelFormatter,
} from '../../../hooks/useDisplayStoreLabel';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import { StoreScheduleStoreExclusionResponse } from '@ydistri/api-sdk';
import { useGetStoreScheduleExclusionsByStoreQuery } from './apiStoreScheduleExclusions';
import { buckets } from './storeScheduleExclusionsLib';

interface BlockProps {
  title: string;
  stores: StoreScheduleStoreExclusionResponse[];
}

/**
 * Shaped like an exception so it reads as one: same bordered, collapsible section, same header
 * size. These carry no drag handle, edit or delete - they are derived from the schedules, not
 * configured here.
 */
const Block: React.FC<BlockProps> = ({ title, stores }) => {
  const formatStoreLabel = useDisplayStoreLabelFormatter();

  if (stores.length === 0) return null;

  return (
    <Section
      collapsible={true}
      open={true}
      $contentMarginTop={false}
      $ratio={0}
      header={`${title} (${stores.length})`}
    >
      <Column $gap="0.25rem">
        {stores.map(store => (
          <Row key={store.store.id} $gap="0.5rem" $flexWrap="nowrap">
            <Text>
              {formatStoreLabel(store.store.name, store.store.customerStoreId, store.store.code)}
            </Text>
            {store.excludedUntil && (
              <Text $type="light">until {formatDate(parseISO(store.excludedUntil))}</Text>
            )}
          </Row>
        ))}
      </Column>
    </Section>
  );
};

/**
 * What the store schedules take out of the proposal, shown next to the user's own exceptions.
 *
 * Read-only and deliberately not configuration rules: these rows are derived from the schedules
 * rather than stored, and putting them in the rule cache would make the client invent overlaps
 * between exceptions that do not overlap.
 */
const ClosedStoresPreview: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: byStore } = useGetStoreScheduleExclusionsByStoreQuery(templateOrCalculation.value);
  const compareStores = useDisplayStoreComparator();

  const split = useMemo(
    () =>
      buckets((byStore ?? []).toSorted((left, right) => compareStores(left.store, right.store))),
    [byStore, compareStores],
  );

  return (
    <>
      <Block title="Closed stores – not receiving" stores={split.notReceiving} />
      <Block title="Closed stores – not sending" stores={split.notSending} />
      <Block title="Completely excluded stores" stores={split.completelyExcluded} />
    </>
  );
};

export default ClosedStoresPreview;
